import { Trans, useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Github, Linkedin, Mail } from 'lucide-react';
import { useCertifications, useEducation, useExperiences } from '@/hooks/use-content';
import SectionEyebrow from './SectionEyebrow';

const About = () => {
  const { t } = useTranslation();
  const { data: experiencesData, isLoading: experiencesLoading } = useExperiences();
  const { data: educationData, isLoading: educationLoading } = useEducation();
  const { data: certificationsData, isLoading: certificationsLoading } = useCertifications();

  return (
    <section id="about" className="py-14 sm:py-20 bg-background">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <SectionEyebrow index="01" label={t('about.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('about.title')}</h2>
          <p className="text-muted-foreground text-lg">{t('about.subtitle')}</p>
        </div>

        <Card className="p-5 sm:p-8 shadow-card">
          <div className="space-y-8">
            <div>
              <p className="text-lg leading-relaxed text-muted-foreground">
                <Trans i18nKey="about.bio" components={{ b: <strong /> }} />
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-primary"></div>
                {t('about.experienceHeading')}
              </h3>

              <div className="space-y-6">
                {experiencesLoading ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <div key={idx} className="space-y-2">
                      <Skeleton className="h-5 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                  ))
                ) : experiencesData && experiencesData.length > 0 ? (
                  experiencesData.map((exp) => (
                    <div key={exp.id}>
                      <p className="font-semibold">{exp.role}</p>
                      <p className="text-muted-foreground">{exp.company}</p>
                      <p className="text-sm text-muted-foreground">{exp.period}</p>
                    </div>
                  ))
                ) : null}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-primary"></div>
                {t('about.educationHeading')}
              </h3>

              {educationLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <Skeleton key={idx} className="h-4 w-2/3" />
                  ))}
                </div>
              ) : (
                educationData?.map((edu) => (
                  <p key={edu.id} className="text-muted-foreground">
                    {edu.title}
                  </p>
                ))
              )}
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-primary"></div>
                {t('about.certificateHeading')}
              </h3>

              {certificationsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <Skeleton key={idx} className="h-4 w-2/3" />
                  ))}
                </div>
              ) : (
                certificationsData?.map((cert) => (
                  <p key={cert.id} className="text-muted-foreground">
                    {cert.title}
                  </p>
                ))
              )}
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-primary"></div>
                {t('about.connectHeading')}
              </h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" size="sm" onClick={() => window.open('https://www.linkedin.com/in/ahmad-abid-1a57a3227/', '_blank')}>
                  <Linkedin className="mr-2 h-4 w-4" />
                  {t('about.linkedin')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open('https://github.com/khanyousafzaideveloper', '_blank')}>
                  <Github className="mr-2 h-4 w-4" />
                  {t('about.github')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.location.href = 'mailto:ahmadyousafzai30@gmail.com'}>
                  <Mail className="mr-2 h-4 w-4" />
                  {t('about.email')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default About;
